import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { ReactComponent as Logo } from "../../assets/img/TOL-Icon.svg";
import { ReactComponent as Arrow } from "../../assets/img/arrow-up-right.svg";
import { ReactComponent as DiscordIcon } from "../../assets/img/discord-mark.svg";
import DownloadAppStore from "../../assets/img/download-appstore.png";
import DownloadGooglePlay from "../../assets/img/download-gplay.png";
import { compareUniverseNames } from "../../service/clubServer";
import FormationPhone from "../../assets/img/image feature 02.png";
import PitchPhone from "../../assets/img/image feature 03.png";
import DiscordPhone from "../../assets/img/image feature 04.png";
import { testAuth } from "../../redux/actions/User";
import { isAuth } from "../../redux/selector/User";
import Seo from '../../components/Seo';
import { PAGE_SEO } from '../../service/seoMeta';
import MetaPixel from "../../components/MetaPixel";
import { clubLogoForTeam } from "../../service/hubClubLogos";
import { HUB_SITE_URL } from "../../service/hubApi";
import {
  fetchLandingData,
  landingLeagueDetails,
} from "../../service/landingData";
import { landingBlogArticles } from "../Blog/blogImages";
import ClubCarousel from "./ClubCarousel";
import { Landing, LandingSelect } from "./landingStyled";

const DISCORD = "https://discord.gg/jAjgAURECT";
const articles = landingBlogArticles();
const questions = [
  [
    "What is The Open League?",
    "The Open League is an online football manager game. Take charge of a club, build your squad, set your tactics and compete against other managers. Follow your club through the app, the website and our Discord community.",
  ],
  [
    "How do I start playing?",
    "Choose Play TOL now to join a club, or download the app for iOS or Android. Already managing a club? Sign in to continue with your existing account.",
  ],
  [
    "Can I play on my phone?",
    "Yes! Download The Open League from the App Store or Google Play. You can also manage your club from this website on mobile.",
  ],
  [
    "Where can I follow matches and results?",
    "Follow your league through the app and website, watch matches in Discord, or explore public standings and results on TOL Hub.",
  ],
  [
    "Where can I get help?",
    "Join our Discord community to meet other managers, ask questions and share feedback. You can also use the contact links below to reach the team.",
  ],
];
const External = ({ children, ...props }) => (
  <a target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);
const track = (name) => {
  try {
    mixpanel.track(name);
  } catch {
    /* Analytics must not block navigation. */
  }
};

function useLeaguePreview() {
  const [servers, setServers] = useState([]);
  const [serverId, setServerId] = useState("");
  const [leagueId, setLeagueId] = useState("");
  const [data, setData] = useState({ table: null, matches: null });
  const [loading, setLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [retry, setRetry] = useState(0);
  const server = servers.find((item) => item._id === serverId);
  const league = server?.leagues?.find((item) => item._id === leagueId);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 15000);
    setCatalogError(false);
    setLoading(true);
    fetchLandingData({ action: "allLeagues" }, controller.signal)
      .then((payload) => {
        if (!active) return;
        const list = Array.isArray(payload.data) ? payload.data : [];
        if (!list.length) throw new Error("No servers available");
        setServers(list);
        const initial = list.find((item) => item.name === "USE2") || list[0];
        setServerId(initial._id);
        setLeagueId(initial.leagues?.[0]?._id || "");
      })
      .catch(() => {
        if (active) {
          setCatalogError(true);
          setLoading(false);
        }
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [retry]);
  useEffect(() => {
    if (!server || !league) {
      if (server) setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 15000);
    setLoading(true);
    setData({ table: null, matches: null });
    fetchLandingData(
      {
        action: "leagueDetails",
        server: server.name,
        leagueId: league._id,
        leagueNumber: Number(league.name?.match(/\d+/)?.[0]) || 1,
      },
      controller.signal
    )
      .then((payload) => {
        if (active) setData(landingLeagueDetails(payload));
      })
      .catch(() => {
        if (active) setData({ table: null, matches: null });
      })
      .finally(() => {
        clearTimeout(timeout);
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [server, league]);
  return {
    servers,
    server,
    league,
    ...data,
    loading,
    catalogError,
    changeServer: (id) => {
      const next = servers.find((item) => item._id === id);
      setServerId(id);
      setLeagueId(next?.leagues?.[0]?._id || "");
    },
    changeLeague: setLeagueId,
    reload: () => setRetry((value) => value + 1),
  };
}

function ClubCrest({ team }) {
  const src = clubLogoForTeam(team?.name) || team?.symbol;
  const [failedSrc, setFailedSrc] = useState(null);
  return src && src !== failedSrc ? (
    <img
      className="table-crest"
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailedSrc(src)}
    />
  ) : (
    <span className="table-crest crest-fallback" aria-hidden="true">
      {team?.name?.slice(0, 1)}
    </span>
  );
}

function PreviewRows({ rows, loading, children, reload, empty }) {
  if (loading)
    return (
      <div className="data-list" aria-label="Loading league data" role="status">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="skeleton" />
        ))}
      </div>
    );
  if (!rows)
    return (
      <div className="empty">
        <p>League data is temporarily unavailable.</p>
        <button className="button outline" onClick={reload}>
          Try again
        </button>
      </div>
    );
  if (!rows.length) return <div className="empty">{empty}</div>;
  return <ol className="data-list">{rows.slice(0, 5).map(children)}</ol>;
}

export function LandingPage({ preview, onLogin }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    servers,
    server,
    league,
    table,
    matches,
    loading,
    reload,
    changeServer,
    changeLeague,
  } = preview;
  const options = (items) =>
    (items || []).map((item) => ({ value: item._id, label: item.name }));
  return (
    <Landing>
      <Seo {...PAGE_SEO.home} />
      <header className="topbar">
        <a className="brand" href="#start" aria-label="The Open League home">
          <Logo viewBox="10 12 44 40" aria-hidden="true" />
          <span>The Open League</span>
        </a>
        <nav
          id="landing-navigation"
          className={`nav ${menuOpen ? "open" : ""}`}
          aria-label="Main navigation"
        >
          {["Tables", "Press", "Blog", "FAQ"].map((label) => (
            <a
              key={label}
              href={`#${label.toLowerCase()}`}
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="account">
          <button className="button outline" onClick={onLogin}>
            Sign In
          </button>
          <External className="button" href={DISCORD}>
            <DiscordIcon aria-hidden="true" />
            Join Discord Server
          </External>
        </div>
        <button
          className="menu-toggle"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          aria-controls="landing-navigation"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          ☰
        </button>
      </header>
      <main id="start">
        <section className="hero wrap" aria-labelledby="hero-title">
          <div>
            <h1 id="hero-title">
              Build your
              <br />
              football empire
            </h1>
            <p className="intro">
              The Open League is a social Football Manager game that brings your
              Discord community to life. Manage your club, compete with friends,
              and dominate the league together.
            </p>
            <div className="numbers">
              <div>
                <strong>3,000+</strong>
                <span>Club Managers</span>
              </div>
              <div>
                <strong>150K+</strong>
                <span>Player Transfers</span>
              </div>
              <div>
                <strong>15K+</strong>
                <span>Matches Simulated</span>
              </div>
            </div>
            <div className="stores">
              <External href="https://play.google.com/store/apps/details?id=com.theopenleague&hl=en_US">
                <img src={DownloadGooglePlay} alt="Get it on Google Play" />
              </External>
              <External href="https://apps.apple.com/br/app/the-open-league/id6593708943?l=en-GB">
                <img src={DownloadAppStore} alt="Download on the App Store" />
              </External>
            </div>
          </div>
          <div className="hero-art">
            <div className="phone pitch">
              <svg
                viewBox="16 7 207 378"
                role="img"
                aria-label="Choose your starting eleven in The Open League app"
              >
                <image href={PitchPhone} width="239" height="412" />
              </svg>
            </div>
            <div className="phone discord">
              <svg
                viewBox="16 7 207 378"
                role="img"
                aria-label="Follow live football matches on Discord"
              >
                <image href={DiscordPhone} width="239" height="412" />
              </svg>
            </div>
            <div className="formation-preview" aria-hidden="true">
              <svg viewBox="16 108 207 172">
                <image href={FormationPhone} width="239" height="412" />
              </svg>
            </div>
          </div>
        </section>
        <div className="hero-divider wrap" />
        <ClubCarousel />
        <section className="section wrap" id="tables">
          <div className="section-head">
            <h2>League tables &amp; Recent Fixtures</h2>
            <p>
              Follow the competition. See who's leading the way and catch up on
              the latest results.
            </p>
          </div>
          <div className="two-col">
            <div className="panel">
              <div className="panel-head">
                <h3>League Standings</h3>
                <External
                  className="text-link"
                  href={`${HUB_SITE_URL}/leagues`}
                >
                  Full table <Arrow />
                </External>
              </div>
              <div className="selectors">
                <LandingSelect
                  aria-label="Server"
                  classNamePrefix="landing-select"
                  options={options(servers).sort((a, b) =>
                    compareUniverseNames(a.label, b.label)
                  )}
                  value={
                    server ? { value: server._id, label: server.name } : null
                  }
                  onChange={(option) => changeServer(option.value)}
                  placeholder="Server"
                  isSearchable
                  menuPlacement="auto"
                />
                <LandingSelect
                  aria-label="League"
                  classNamePrefix="landing-select"
                  options={options(server?.leagues)}
                  value={
                    league ? { value: league._id, label: league.name } : null
                  }
                  onChange={(option) => changeLeague(option.value)}
                  placeholder="League"
                  isSearchable={false}
                  menuPlacement="auto"
                />
              </div>
              <PreviewRows
                rows={table}
                loading={loading}
                reload={reload}
                empty="No standings available yet."
              >
                {(team, index) => (
                  <li className="data-row" key={team._id || index}>
                    <span className="rank">{index + 1}</span>
                    <span className="team-name">
                      <ClubCrest team={team} />
                      {team.name}
                    </span>
                    <span className="points">{team.points} pts</span>
                  </li>
                )}
              </PreviewRows>
              <p className="data-note">Public standings provided by TOL Hub.</p>
            </div>
            <div className="panel fixtures">
              <div className="panel-head">
                <h3>Recent Fixtures</h3>
                <External
                  className="text-link"
                  href={`${HUB_SITE_URL}/leagues`}
                >
                  All results <Arrow />
                </External>
              </div>
              <PreviewRows
                rows={matches}
                loading={loading}
                reload={reload}
                empty="No completed matches in this league yet."
              >
                {(match, index) => (
                  <li className="data-row" key={match.id || index}>
                    <div className="fixture-copy">
                      <span>
                        <span className="fixture-team">
                          <ClubCrest team={match.home_team} />
                          {match.home_team?.name}
                        </span>
                        <span className="fixture-team">
                          <ClubCrest team={match.away_team} />
                          {match.away_team?.name}
                        </span>
                      </span>
                      <div className="tags">
                        <span>{server?.name}</span>
                        <span>{league?.name}</span>
                        {!Number.isNaN(Date.parse(match.date)) && (
                          <time dateTime={match.date}>
                            {new Date(match.date).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                            })}
                          </time>
                        )}
                      </div>
                    </div>
                    <span className="result-score">
                      {match.home_goals} – {match.away_goals}
                    </span>
                  </li>
                )}
              </PreviewRows>
            </div>
          </div>
        </section>
        <section className="section wrap" id="press">
          <div className="section-head">
            <h2>Press &amp; Coverage</h2>
            <p>
              Get to know The Open League, explore the community and find
              resources for your next story.
            </p>
          </div>
          <div className="three-col">
            <article className="editorial">
              <span className="eyebrow">Media resources</span>
              <h3>Meet The Open League</h3>
              <p>
                Explore the game and download official logos, club crests and
                screenshots.
              </p>
              <Link className="text-link" to="/press-kit">
                Explore the press kit <Arrow />
              </Link>
            </article>
            <article className="editorial">
              <span className="eyebrow">The community</span>
              <h3>A world of football managers</h3>
              <p>
                Explore clubs, league tables and match results from across the
                TOL universe.
              </p>
              <External className="text-link" href={HUB_SITE_URL}>
                Visit TOL Hub <Arrow />
              </External>
            </article>
            <article className="editorial">
              <span className="eyebrow">Get in touch</span>
              <h3>Have a story in mind?</h3>
              <p>
                Contact the team for press enquiries, game information and media
                resources.
              </p>
              <a className="text-link" href="mailto:Chris@theopenleague.com">
                Contact us <Arrow />
              </a>
            </article>
          </div>
          <div className="section-cta">
            <External className="button" href={DISCORD}>
              Join the community <Arrow />
            </External>
          </div>
        </section>
        <section className="section wrap" id="blog">
          <div className="section-head">
            <h2>From the Blog</h2>
            <p>
              Discover the world of football management, on and off the virtual
              pitch.
            </p>
          </div>
          <div className="three-col">
            {articles.map((article) => (
              <article className="post" key={article.title}>
                <div className="post-art">
                  <img src={article.image} loading="lazy" alt="" />
                </div>
                <div className="post-copy">
                  <span className="eyebrow">Football management</span>
                  <h3>{article.title}</h3>
                  <p>{article.description}</p>
                  <Link className="text-link" to={`/blog/${article.slug}`}>
                    Read more <Arrow />
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="section-cta">
            <button className="button" onClick={onLogin}>
              Play TOL now <Arrow />
            </button>
          </div>
        </section>
        <section className="section faq-section wrap" id="faq">
          <div className="section-head">
            <h2>Frequently asked questions</h2>
            <p>Everything you need to get started.</p>
          </div>
          <div className="faq-list">
            {questions.map(([question, answer], index) => (
              <details key={question} open={index === 0 ? true : undefined}>
                <summary>
                  {question}
                  <span aria-hidden="true">⌄</span>
                </summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      <footer className="footer">
        <div className="wrap footer-grid">
          <div>
            <h3>The Open League</h3>
            <p>
              Build your club. Find your community.
              <br />
              Write your own football story.
            </p>
          </div>
          <div>
            <h3>Resources</h3>
            <a href="#faq">FAQ</a>
            <Link to="/blog">Blog</Link>
            <Link to="/press-kit">Press kit</Link>
            <External href={HUB_SITE_URL}>TOL Hub</External>
          </div>
          <div>
            <h3>Company &amp; Community</h3>
            <a href="mailto:Chris@theopenleague.com">Contact us</a>
            <External href={DISCORD}>Discord</External>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/delete-account">Delete account</Link>
            <Link to="/terms">Terms of service</Link>
          </div>
        </div>
        <div className="copyright">
          © {new Date().getFullYear()} The Open League. All rights reserved.
        </div>
      </footer>
    </Landing>
  );
}

export default function StartPage() {
  const history = useHistory();
  const dispatch = useDispatch();
  const authenticated = useSelector(isAuth);
  const preview = useLeaguePreview();
  useEffect(() => {
    if (authenticated === null) dispatch(testAuth());
    if (authenticated) history.replace("/home");
  }, [authenticated, dispatch, history]);
  const login = () => {
    track("Login clicked");
    window.location.href = process.env.REACT_APP_AUTH_LINK;
  };
  return (
    <>
      <LandingPage preview={preview} onLogin={login} />
      <MetaPixel />
    </>
  );
}
